<template>
  <div class="home-page">
    <div class="ambient-shape ambient-left"></div>
    <div class="ambient-shape ambient-right"></div>

    <header class="hero-panel reveal">
      <p class="hero-tag">Khazanah Semantik Nusantara</p>
      <h1 class="hero-title">Ontologi Wayang Purwa</h1>
      <p class="hero-description">
        Menyajikan informasi semantik mengenai tokoh, pusaka, kesaktian, dan tempat dalam dunia Wayang Purwa berbasis teknologi ontologi dan web semantik.
      </p>
      <div class="hero-ribbon">RDF • SPARQL • DBpedia</div>
    </header>

    <main class="content-stack">
      <section class="panel reveal delay-1">
        <div class="panel-head">
          <h2 class="panel-title">Ringkasan Data Ontologi</h2>
          <span class="panel-chip">Live data</span>
        </div>

        <div v-if="loading" class="state-box">Memuat ringkasan data...</div>
        <div v-else-if="error" class="state-box state-error">{{ error }}</div>
        <div v-else class="stats-grid">
          <article class="stat-card">
            <span class="stat-label">Total Tokoh</span>
            <strong class="stat-value">{{ totalTokoh }}</strong>
          </article>
          <article class="stat-card">
            <span class="stat-label">Total Tempat Asal</span>
            <strong class="stat-value">{{ totalTempatAsal }}</strong>
          </article>
          <article class="stat-card">
            <span class="stat-label">Total Pusaka</span>
            <strong class="stat-value">{{ totalPusaka }}</strong>
          </article>
          <article class="stat-card">
            <span class="stat-label">Total Kesaktian</span>
            <strong class="stat-value">{{ totalKesaktian }}</strong>
          </article>
        </div>
      </section>

      <section class="panel reveal delay-2">
        <div class="panel-head">
          <h2 class="panel-title">Mengenal Wayang Kulit Purwa</h2>
          <button @click="showWayangInfo = !showWayangInfo" class="toggle-btn">
            {{ showWayangInfo ? 'Sembunyikan' : 'Tampilkan' }}
          </button>
        </div>

        <div v-show="showWayangInfo">
          <div v-if="loadingDbpedia" class="state-box">Mengambil data dari DBpedia...</div>
          <div v-else-if="errorDbpedia" class="state-box state-error">{{ errorDbpedia }}</div>
          <div v-else class="article-list">
            <article v-for="item in wayangDbpedia" :key="item.label" class="article-card">
              <h3 class="article-title">{{ item.label }}</h3>
              <a :href="item.uri" class="article-link" target="_blank" rel="noopener noreferrer">{{ item.uri }}</a>
              <p class="article-text"><strong>Abstrak:</strong> {{ item.abstract }}</p>
              <p class="article-text"><strong>Komentar:</strong> {{ item.comment }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="panel reveal delay-3">
        <div class="panel-head">
          <h2 class="panel-title">Cerita Mahabharata dan Ramayana</h2>
          <button @click="showWayangStories = !showWayangStories" class="toggle-btn">
            {{ showWayangStories ? 'Sembunyikan' : 'Tampilkan' }}
          </button>
        </div>

        <div v-show="showWayangStories" class="article-list stories-grid">
          <article v-for="story in storyDbpedia" :key="story.label" class="article-card story-card">
            <h3 class="article-title">{{ story.label }}</h3>
            <a :href="story.uri" class="article-link" target="_blank" rel="noopener noreferrer">{{ story.uri }}</a>
            <p class="article-text"><strong>Abstrak:</strong> {{ story.abstract }}</p>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'HomeView',
  data() {
    return {
      totalTokoh: 0,
      totalTempatAsal: 0,
      totalPusaka: 0,
      totalKesaktian: 0,
      loading: true,
      error: null,
      wayangDbpedia: [],
      storyDbpedia: [],
      showWayangInfo: true,
      showWayangStories: true,
      loadingDbpedia: true,
      errorDbpedia: null
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchLocalData(),
      this.fetchDbpediaData(),
      this.fetchWayangStories()
    ])
  },
  methods: {
    async queryDbpedia(query) {
      const dbpediaEndpoint = 'https://dbpedia.org/sparql'
      const headers = { Accept: 'application/sparql-results+json' }

      const attempts = [
        () => axios.get('http://localhost:8081/proxy', {
          params: { url: dbpediaEndpoint, query, format: 'json' },
          headers,
          timeout: 12000
        }),
        () => axios.get(`http://localhost:8081/${dbpediaEndpoint}`, {
          params: { query, format: 'json' },
          headers,
          timeout: 12000
        }),
        () => axios.get(dbpediaEndpoint, {
          params: { query, format: 'json' },
          headers,
          timeout: 12000
        })
      ]

      let lastError = null

      for (const request of attempts) {
        try {
          const response = await request()
          return response.data
        } catch (error) {
          lastError = error
        }
      }

      throw lastError
    },
    async fetchLocalData() {
      try {
        const endpoint = 'https://app.alunalun.info/fuseki/wayangpurwa/sparql'
        const headers = { Accept: 'application/sparql-results+json' }

        const getCount = async (query) => {
          const response = await axios.get(endpoint, {
            params: { query, format: 'json' },
            headers
          })
          return response.data.results.bindings.length
        }

        const queryTokoh = `
          PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
          PREFIX wp: <http://ukdw.ac.id/ontologi/wayangpurwa#>
          PREFIX wpo: <http://ontology/wayangpurwa#>
          SELECT ?tokoh ?tipe WHERE {
            ?tokoh rdf:type ?tipe .
            FILTER(?tipe IN (
              wp:Dewa, wp:Bidadari, wp:Kera, wp:MakhlukHalus,
              wp:Manusia, wp:Raksasa, wp:Siluman,
              wpo:Dewa, wpo:Bidadari, wpo:Kera, wpo:MakhlukHalus,
              wpo:Manusia, wpo:Raksasa, wpo:Siluman
            ))
          }
        `

        const queryTempatAsal = `
          PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
          SELECT ?tempat ?tipe WHERE {
            ?tempat rdf:type ?tipe .
            FILTER regex(str(?tipe), "Kerajaan|Keputren|Kesatriyan|Laut|Padhukuhan|Pertapaan|Sungai|Kahyangan")
          }
          ORDER BY ?tipe ?tempat
        `

        const queryPusaka = `
          PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
          PREFIX wpo: <http://ontology/wayangpurwa#>
          SELECT ?individu WHERE {
            ?individu rdf:type wpo:Pusaka .
          }
        `

        const queryKesaktian = `
          PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
          PREFIX wpo: <http://ontology/wayangpurwa#>
          SELECT ?individu WHERE {
            ?individu rdf:type wpo:Kesaktian .
          }
        `

        this.totalTokoh = await getCount(queryTokoh)
        this.totalTempatAsal = await getCount(queryTempatAsal)
        this.totalPusaka = await getCount(queryPusaka)
        this.totalKesaktian = await getCount(queryKesaktian)
      } catch (err) {
        console.error(err)
        this.error = 'Gagal mengambil ringkasan data.'
      } finally {
        this.loading = false
      }
    },

    async fetchDbpediaData() {
      try {
        const queryID = `
          PREFIX dbo: <http://dbpedia.org/ontology/>
          PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
          SELECT ?abstract ?comment WHERE {
            <http://dbpedia.org/resource/Wayang_kulit> dbo:abstract ?abstract ;
                                                        rdfs:comment ?comment .
            FILTER (lang(?abstract) = "id" && lang(?comment) = "id")
          } LIMIT 1
        `

        const queryEN = `
          PREFIX dbo: <http://dbpedia.org/ontology/>
          PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
          SELECT ?abstract ?comment WHERE {
            <http://dbpedia.org/resource/Wayang_kulit> dbo:abstract ?abstract ;
                                                        rdfs:comment ?comment .
            FILTER (lang(?abstract) = "en" && lang(?comment) = "en")
          } LIMIT 1
        `

        let res = await this.queryDbpedia(queryID)
        let result = res.results.bindings?.[0]

        if (!result) {
          res = await this.queryDbpedia(queryEN)
          result = res.results.bindings?.[0]
        }

        this.wayangDbpedia = [{
          label: 'Wayang kulit',
          uri: 'http://dbpedia.org/resource/Wayang_kulit',
          abstract: result?.abstract?.value || 'Tidak ditemukan abstrak.',
          comment: result?.comment?.value || 'Tidak ditemukan komentar.'
        }]
      } catch (err) {
        console.error(err)
        this.errorDbpedia = 'Gagal mengambil data dari DBpedia. Pastikan proxy lokal aktif (node proxy.js atau node server.js) lalu coba lagi.'
      } finally {
        this.loadingDbpedia = false
      }
    },

    async fetchWayangStories() {
      const stories = [
        { label: 'Mahabharata', uri: 'http://dbpedia.org/resource/Mahabharata' },
        { label: 'Ramayana', uri: 'http://dbpedia.org/resource/Ramayana' }
      ]

      for (const story of stories) {
        try {
          const query = `
            PREFIX dbo: <http://dbpedia.org/ontology/>
            SELECT ?abstract WHERE {
              <${story.uri}> dbo:abstract ?abstract .
              FILTER(lang(?abstract) = "en")
            } LIMIT 1
          `
          const res = await this.queryDbpedia(query)
          const result = res.results.bindings?.[0]
          this.storyDbpedia.push({
            label: story.label,
            uri: story.uri,
            abstract: result?.abstract?.value || 'Tidak ditemukan abstrak.'
          })
        } catch (error) {
          console.error(`Gagal mengambil abstrak untuk ${story.label}`, error)
          this.storyDbpedia.push({
            label: story.label,
            uri: story.uri,
            abstract: 'Gagal mengambil data dari DBpedia.'
          })
        }
      }
    }

  }
}
</script>

<style scoped>
:root {
  --bg-cream: #fff8e8;
  --bg-warm: #ffe7bd;
  --ink-strong: #2b1c0f;
  --ink-soft: #5f4a37;
  --panel: #fffef9;
  --panel-border: #efcf9a;
  --accent: #b45309;
  --accent-soft: #fde4ba;
  --sky: #9bd4e4;
}

.home-page {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  padding: 30px;
  background: radial-gradient(circle at 10% 10%, #fff7df 0%, #fff3d1 38%, #ffe8b7 100%);
  font-family: "Merriweather", "Palatino Linotype", "Book Antiqua", serif;
  color: var(--ink-strong);
}

.ambient-shape {
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(0.5px);
}

.ambient-left {
  width: 320px;
  height: 320px;
  left: -120px;
  top: -100px;
  background: radial-gradient(circle, rgba(155, 212, 228, 0.5), rgba(155, 212, 228, 0));
}

.ambient-right {
  width: 420px;
  height: 420px;
  right: -170px;
  top: 180px;
  background: radial-gradient(circle, rgba(253, 228, 186, 0.95), rgba(253, 228, 186, 0));
}

.hero-panel {
  position: relative;
  z-index: 1;
  margin: 0 auto;
  max-width: 1100px;
  padding: 36px;
  border-radius: 26px;
  border: 1px solid rgba(239, 207, 154, 0.8);
  background: linear-gradient(140deg, rgba(255, 251, 240, 0.95), rgba(255, 240, 204, 0.95));
  box-shadow: 0 20px 44px rgba(141, 88, 24, 0.15);
}

.hero-tag {
  margin: 0;
  font-size: 13px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.hero-title {
  margin: 12px 0;
  font-size: clamp(2rem, 3vw, 3.2rem);
  line-height: 1.15;
}

.hero-description {
  margin: 0;
  max-width: 780px;
  font-size: 1.05rem;
  line-height: 1.75;
  color: var(--ink-soft);
}

.hero-ribbon {
  margin-top: 18px;
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #7c4700;
  background: linear-gradient(120deg, #ffe8bc, #fff3d7);
  border: 1px solid #f3cd84;
}

.content-stack {
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 20px auto 0;
  display: grid;
  gap: 18px;
}

.panel {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 20px;
  padding: 22px;
  box-shadow: 0 14px 34px rgba(121, 74, 20, 0.08);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-title {
  margin: 0;
  font-size: clamp(1.15rem, 2vw, 1.5rem);
}

.panel-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #fef3c7;
  color: #8a4b00;
  border: 1px solid #f3cb83;
}

.toggle-btn {
  border: 1px solid #d0a35c;
  background: linear-gradient(120deg, #ffe3af, #ffedca);
  color: #7b4600;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 0.83rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.toggle-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(138, 75, 0, 0.18);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.stat-card {
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(145deg, #fff8e8, #fff2d5);
  border: 1px solid #f1cd90;
  animation: rise 0.55s ease both;
}

.stat-card:nth-child(2) {
  animation-delay: 0.08s;
}

.stat-card:nth-child(3) {
  animation-delay: 0.16s;
}

.stat-card:nth-child(4) {
  animation-delay: 0.24s;
}

.stat-label {
  display: block;
  font-size: 0.82rem;
  color: #8a6743;
  margin-bottom: 7px;
}

.stat-value {
  font-size: clamp(1.4rem, 3vw, 2rem);
  color: #7c3608;
}

.article-list {
  display: grid;
  gap: 12px;
}

.stories-grid {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.article-card {
  padding: 16px;
  border-radius: 14px;
  border: 1px solid #edd0a2;
  background: #fffdf7;
}

.story-card {
  height: 100%;
}

.article-title {
  margin: 0;
  font-size: 1.1rem;
}

.article-link {
  display: inline-block;
  margin-top: 6px;
  color: #0e7490;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
  word-break: break-word;
}

.article-text {
  margin: 10px 0 0;
  line-height: 1.7;
  color: #5e4633;
}

.state-box {
  border: 1px dashed #e3bc7f;
  border-radius: 12px;
  padding: 14px;
  background: #fff7e5;
  color: #7d4a11;
}

.state-error {
  border-color: #f0a39a;
  background: #fff2f0;
  color: #9f2318;
}

.reveal {
  animation: fadeUp 0.65s ease both;
}

.delay-1 {
  animation-delay: 0.08s;
}

.delay-2 {
  animation-delay: 0.16s;
}

.delay-3 {
  animation-delay: 0.24s;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .home-page {
    padding: 18px;
  }

  .hero-panel {
    padding: 24px;
  }

  .panel {
    padding: 16px;
  }

  .panel-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
